/**
 * ProviderIdentitySection — read-only identity dl + admin inline edit form.
 * Reuses ProviderFormFields (schemas hidden) and the same uniqueness checks as create.
 */

import { useEffect, useState } from "react";
import { Pencil } from "lucide-react";
import { Button, AlertMessage } from "@/components/reusable";
import { ProviderFormFields } from "@/components/providers/ProviderFormFields";
import type { UniquenessState } from "@/components/providers/ProviderFormFields";
import { updateProvider, checkProviderName, checkProviderShortCode } from "@/services/providersApi";
import { useTelemetry } from "@/hooks/useTelemetry";
import { cn } from "@/lib/utils";
import { PROVIDER_TYPE_LABELS } from "@/types/providers";
import type { ProviderOut, ProviderType } from "@/types/providers";

const SHORT_CODE_RE = /^[A-Z0-9-]{1,10}$/;

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

interface ProviderIdentitySectionProps {
  provider: ProviderOut;
  isAdmin: boolean;
  onProviderUpdated: (updated: ProviderOut) => void;
}

/** Identity display / edit block for the Provider Profile Overview tab. */
export function ProviderIdentitySection({
  provider,
  isAdmin,
  onProviderUpdated,
}: ProviderIdentitySectionProps) {
  const { track, trackError } = useTelemetry();

  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(provider.provider_name);
  const [editShortCode, setEditShortCode] = useState(provider.provider_short_code);
  const [editType, setEditType] = useState<ProviderType | "">(
    provider.provider_type as ProviderType
  );
  const [nameUniqueness, setNameUniqueness] = useState<UniquenessState>("idle");
  const [shortCodeUniqueness, setShortCodeUniqueness] = useState<UniquenessState>("idle");
  const [shortCodeFormatError, setShortCodeFormatError] = useState<string | null>(null);
  const [savingIdentity, setSavingIdentity] = useState(false);
  const [identityError, setIdentityError] = useState<string | null>(null);

  // Keep edit form in sync when parent provider updates (e.g. after save)
  useEffect(() => {
    if (!editing) {
      setEditName(provider.provider_name);
      setEditShortCode(provider.provider_short_code);
      setEditType(provider.provider_type as ProviderType);
    }
  }, [provider, editing]);

  function startEditing() {
    setEditName(provider.provider_name);
    setEditShortCode(provider.provider_short_code);
    setEditType(provider.provider_type as ProviderType);
    setNameUniqueness("idle");
    setShortCodeUniqueness("idle");
    setShortCodeFormatError(null);
    setIdentityError(null);
    setEditing(true);
  }

  function cancelEditing() {
    setEditing(false);
    setIdentityError(null);
  }

  async function handleNameBlur() {
    const trimmed = editName.trim();
    if (!trimmed || trimmed === provider.provider_name) {
      setNameUniqueness("idle");
      return;
    }
    setNameUniqueness("checking");
    try {
      const result = await checkProviderName(trimmed, provider.provider_id);
      setNameUniqueness(result.available ? "available" : "taken");
    } catch {
      setNameUniqueness("idle");
    }
  }

  async function handleShortCodeBlur() {
    const trimmed = editShortCode.trim();
    if (!trimmed) return;
    if (!SHORT_CODE_RE.test(trimmed)) {
      setShortCodeFormatError("Must be 1–10 uppercase letters, numbers, or hyphens.");
      setShortCodeUniqueness("idle");
      return;
    }
    setShortCodeFormatError(null);
    if (trimmed === provider.provider_short_code) {
      setShortCodeUniqueness("idle");
      return;
    }
    setShortCodeUniqueness("checking");
    try {
      const result = await checkProviderShortCode(trimmed, provider.provider_id);
      setShortCodeUniqueness(result.available ? "available" : "taken");
    } catch {
      setShortCodeUniqueness("idle");
    }
  }

  async function handleSaveIdentity() {
    if (nameUniqueness === "taken" || shortCodeUniqueness === "taken" || shortCodeFormatError) {
      return;
    }
    if (!editName.trim() || !editShortCode.trim() || !editType) return;
    setSavingIdentity(true);
    setIdentityError(null);
    try {
      const updated = await updateProvider(provider.provider_id, {
        provider_name: editName.trim(),
        provider_short_code: editShortCode.trim(),
        provider_type: editType as ProviderType,
      });
      track("provider_identity_updated", { providerId: provider.provider_id });
      onProviderUpdated(updated);
      setEditing(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to update provider.";
      setIdentityError(msg);
      trackError(err instanceof Error ? err : new Error(msg), {
        context: "provider_identity_update",
      });
    } finally {
      setSavingIdentity(false);
    }
  }

  return (
    <section>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--color-text-tertiary)]">
          Identity
        </h2>
        {isAdmin && !editing && (
          <Button type="button" variant="secondary" size="sm" onClick={startEditing}>
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </Button>
        )}
      </div>

      {identityError && (
        <div className="mb-3">
          <AlertMessage variant="error" message={identityError} />
        </div>
      )}

      {editing ? (
        <div className="rounded-xl border border-[var(--color-border-light)] bg-white p-5">
          <ProviderFormFields
            name={editName}
            onNameChange={(v) => {
              setEditName(v);
              setNameUniqueness("idle");
            }}
            onNameBlur={handleNameBlur}
            nameUniqueness={nameUniqueness}
            nameError={null}
            shortCode={editShortCode}
            onShortCodeChange={(v) => {
              setEditShortCode(v);
              setShortCodeUniqueness("idle");
              setShortCodeFormatError(null);
            }}
            onShortCodeBlur={handleShortCodeBlur}
            shortCodeUniqueness={shortCodeUniqueness}
            shortCodeError={shortCodeFormatError}
            providerType={editType}
            onProviderTypeChange={(v) => setEditType(v)}
            selectedSchemaIds={[]}
            onSchemaToggle={() => {}}
            schemas={[]}
            schemasLoading={false}
            showSchemas={false}
            disabled={savingIdentity}
          />
          <div className="mt-6 flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={cancelEditing}
              disabled={savingIdentity}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleSaveIdentity}
              disabled={
                savingIdentity ||
                !editName.trim() ||
                !editShortCode.trim() ||
                !editType ||
                nameUniqueness === "taken" ||
                shortCodeUniqueness === "taken" ||
                !!shortCodeFormatError
              }
            >
              {savingIdentity ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      ) : (
        <dl className="grid grid-cols-2 gap-x-8 gap-y-4 sm:grid-cols-3">
          {[
            { label: "Provider Name", value: provider.provider_name },
            { label: "Short Code", value: provider.provider_short_code, mono: true },
            {
              label: "Provider Type",
              value:
                PROVIDER_TYPE_LABELS[provider.provider_type as ProviderType] ??
                provider.provider_type,
            },
            { label: "Created", value: formatDate(provider.created_at) },
            { label: "Activated", value: formatDate(provider.activated_at) },
            { label: "Last Submission", value: formatDate(provider.last_submission_at) },
          ].map(({ label, value, mono }) => (
            <div key={label}>
              <dt className="text-xs font-medium text-[var(--color-text-tertiary)]">{label}</dt>
              <dd
                className={cn(
                  "mt-0.5 text-sm text-[var(--color-text-primary)]",
                  mono && "font-mono"
                )}
              >
                {value}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}
